import { assertAPIResponse } from "../services/api-response";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../services/toast";
import axiosInstance from "../services/axiosInstance";
import { axiosWithAbort } from "../services/axiosWithAbort";

const baseURL = axiosInstance.defaults.baseURL;

export const reportGenerationAPI = {
  //////// 取得GHG Inventory task id
  async getGHGInventoryTaskId(
    version,
    userId,
    reportNum,
    lang,
    gwpId,
    selectedYear,
    selectedCom,
    selectedCates,
  ) {
    // 實際要使用的參數
    const requestBody = {
      sample: version,
      userId: userId,
      reportId: reportNum,
      year: selectedYear,
      lang: lang,
      gwpId: gwpId,
      companyIds: selectedCom,
      category: selectedCates,
    };

    try {
      const exportResponse = await axiosInstance.post(
        `/api/inventory/export-ghg-inventory`,
        requestBody,
      );
      // 獲取並返回 taskId
      const taskId = exportResponse.data.data.task_id;
      return { success: true, taskId };
    } catch (error) {
      console.error("發起匯出任務時發生錯誤:", error);
      throw error;
    }
  },

  //匯出GHG Inventory
  async downloadGHGInventory(
    version,
    userId,
    reportNum,
    lang,
    gwpId,
    selectedYear,
    selectedCom,
    selectedCates,
    t = (key) => key, //翻譯
    onProgress = null, // 新增進度回調
  ) {
    try {
      //  發起任務
      if (onProgress) onProgress({ stage: "initiating", progress: 0 });
      //去呼叫後端API
      const exportResult = await this.getGHGInventoryTaskId(
        version,
        userId,
        reportNum,
        lang,
        gwpId,
        selectedYear,
        selectedCom,
        selectedCates,
      );

      if (!exportResult.success) {
        throw new Error("Failed to start GHG inventory export");
      }

      // 輪詢taskId並下載
      if (onProgress) onProgress({ stage: "processing", progress: 10 });
      const downloadResult = await this.downloadGHGInventoryFile(
        exportResult.taskId,
        version,
        onProgress, // 傳遞進度回調
      );
      if (downloadResult && downloadResult.success === true) {
        if (onProgress) onProgress({ stage: "completed", progress: 100 });
        showSuccessToast({
          apiData: downloadResult.apiData,
          scenarioCode: MESSAGE_CODES.REPORT_DOWNLOAD_SUCCESS,
        });
        return true;
      } else {
        return false;
      }
    } catch (error) {
      handleAPIError(error, {
        scenarioCode: error?.scenarioCode,
        throwError: false,
      });
      console.error("Error fetching ghg inventory file.", error);
      return false;
    }
  },

  // 取得task_id後下載檔案
  async downloadGHGInventoryFile(taskId, version, onProgress = null) {
    try {
      let retryCount = 0;
      const MAX_RETRIES = 100;
      let POLLING_INTERVAL = 3000; // 3秒
      let downloadName = "";
      let completionAPIData = null;
      let abortController = new AbortController(); // 支援取消請求

      while (retryCount < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));

        const statusResponse = await axiosInstance.get(
          `/api/inventory/check-task-status/${taskId}`,
          { signal: abortController.signal },
        );
        const status = statusResponse.data.data.status;
        const progress = statusResponse.data.data.progress || 0; // 假設後端有回傳進度

        // 更新進度
        if (onProgress) {
          onProgress({
            stage: "processing",
            progress: 10 + progress * 0.8, // 10-90%
            attempt: retryCount + 1,
          });
        }

        if (status === "success") {
          downloadName = statusResponse.data.data.export_name;
          completionAPIData = statusResponse.data;
          break;
        } else if (status === "failed") {
          const error = new Error("GHG inventory export failed");
          error.response = { data: statusResponse.data };
          throw error;
        }

        retryCount++;
        // 動態調整輪詢間隔 (前5次快速檢查,之後放慢)
        if (retryCount > 5) {
          POLLING_INTERVAL = 5000; // 改為5秒
        }
      }

      if (retryCount >= MAX_RETRIES) {
        const error = new Error("GHG inventory export timed out");
        error.scenarioCode = MESSAGE_CODES.REPORT_TIMEOUT;
        throw error;
      }

      // 下載階段
      if (onProgress) onProgress({ stage: "downloading", progress: 90 });
      const isPowerPoint = version === "tgo_ppt";
      const downloadEndpoint = isPowerPoint
        ? `/api/inventory/check-task-status/download/ppt/${taskId}`
        : `/api/inventory/check-task-status/download/${taskId}`;
      const response = await axiosInstance.get(
        downloadEndpoint,
        { responseType: "blob", timeout: 0 },
      );

      // 建立 Blob 物件並下載
      const fileExtension = isPowerPoint ? "pptx" : "xlsx";
      const mimeType = isPowerPoint
        ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([response.data], { type: mimeType });

      const downloadUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      const baseDownloadName = (downloadName || "GHG Inventory").replace(
        /\.(xlsx|pptx)$/i,
        "",
      );
      downloadLink.href = downloadUrl;
      downloadLink.download = `${baseDownloadName}.${fileExtension}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, apiData: completionAPIData };
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("下載已取消");
        return { success: false, cancelled: true };
      }
      console.error("檢查狀態或下載 GHG 清單檔案時發生錯誤:", error);
      throw error;
    }
  },

  async downloadGHGReport(
    version,
    userId,
    reportNum,
    lang,
    gwpId,
    year,
    companyIds,
    category,
  ) {
    const requestBody = {
      sample: version,
      userId: userId,
      reportId: reportNum,
      lang: lang,
      gwpId: gwpId,
      year: year,
      companyIds: companyIds,
      category: category,
    };
    try {
      const response = await axiosInstance.post(
        `/api/report/download_report`,
        requestBody,
        {
          responseType: "blob",
          timeout: 300000,
        },
      );

      return response;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //取得報告書編號
  async getReportNumberAPI(userId) {
    try {
      const response = await axiosInstance.get(
        `/api/report/get_report_id?userId=${userId}`,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //取得已設定年份下拉式清單
  async getReportYearDropdownAPI(userId, reportId) {
    try {
      const response = await axiosInstance.get(
        `/api/report/get_report_year_dropdown?userId=${userId}&reportId=${reportId}`,
      );
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //取得報告書列表
  async getReportListAPI(companyId, lang, gwpId, year, reportId) {
    try {
      const response = await axiosInstance.get(
        `/api/report/get_report_list?companyId=${companyId}&reportId=${reportId}&year=${year}&gwp=${gwpId}&lang=${lang}`,
      );
      // 根據 API 實際回傳結構調整
      let fileList = response.data.data || [];

      // 在這裡添加基礎URL到每個檔案的link屬性
      fileList = fileList.map((file) => ({
        ...file,
        path: `${baseURL}${file.path}`,
      }));
      return fileList;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //上傳報告書驗證檔案
  async uploadReportVerificationAPI(
    reportId,
    year,
    gwpId,
    companyId,
    lang,
    file,
  ) {
    const formData = new FormData();
    formData.append("file", file[0]);

    try {
      const response = await axiosInstance.post(
        `/api/report/validation_file`,
        formData,
        {
          params: {
            year,
            reportId,
            companyId,
            language: lang,
            gwp: gwpId,
          },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      assertAPIResponse(response);
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //更新報告書查證版本
  async updateReportVerificationAPI(reportId) {
    const requestBody = {
      isVerification: true,
    };
    try {
      const response = await axiosInstance.post(
        `/api/report/verification_status/${reportId}`,
        requestBody,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error(error);
    }
  },

  //刪除報告書
  async deleteReportAPI(id) {
    //reportId
    try {
      const response = await axiosInstance.delete(`/api/report/${id}`);
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error deleting report:", error);
    }
  },

  //下載list中的報告書
  async downloadReportVerificationAPI(fileLink) {
    try {
      const response = await axiosInstance.get(fileLink, {
        responseType: "blob",
        skipAuth: true,
      });

      return response.data; // ✅ 回傳 blob 給外部處理
    } catch (error) {
      handleAPIError(error);
      console.error("API 下載失敗:", error);
    }
  },

  //預覽下載list中的報告書
  async previewWordFileAPI(link) {
    try {
      // 獲取文檔內容
      const response = await axiosInstance.get(link, {
        responseType: "blob",
        skipAuth: true,
      });

      // 將 Blob 轉換成 ArrayBuffer
      const arrayBuffer = await response.data.arrayBuffer();
      return arrayBuffer;
    } catch (error) {
      handleAPIError(error);
      console.error("文檔預覽 API 調用出錯:", error);
    }
  },

  ////取得通過重大性評估的類別
  async getAvailableCategory(rootId, year, standardId) {
    const response = await axiosWithAbort("getAvailableCategory", {
      method: "POST",
      url: `/api/boundary/organization_boundary/get_available_category/${rootId}/${year}/${standardId}`,
    });
    if (response === null) return null; // request was aborted
    return response.data.data;
  },

  //////8550
  async downloadGHGReport8550(
    version,
    userId,
    reportNum,
    lang,
    gwpId,
    year,
    companyIds,
    category,
  ) {
    const requestBody = {
      sample: version,
      userId: userId,
      reportId: reportNum,
      lang: lang,
      gwpId: gwpId,
      year: year,
      companyIds: companyIds,
      category: category,
    };
    try {
      const response = await axiosInstance.post(
        `/api/report/trigger_report`,
        requestBody,
      );

      return response.data.data;
    } catch (error) {
      handleAPIError(error);
      console.error("There  was an error triggering the report:", error);
    }
  },
};
